import { INITIAL_UPLOAD_STATE } from '@/features/mcap/config/upload';
import type { UploadEvent, UploadState } from '@/features/mcap/types/upload';

export type { UploadEvent, UploadState } from '@/features/mcap/types/upload';

export function reduceUploadState(state: UploadState, event: UploadEvent): UploadState {
  if (event.type === 'cancel' && !['accepted', 'rejected', 'failed'].includes(state.phase)) {
    return { ...state, phase: 'canceled' };
  }
  if (event.type === 'retryable_failure') {
    return { ...state, phase: 'paused_retryable', errorCode: event.code };
  }
  if (event.type === 'fatal_failure') {
    return { ...state, phase: 'failed', errorCode: event.code };
  }
  if (event.type === 'rejected') {
    return { ...state, phase: 'rejected', errorCode: event.code };
  }

  switch (state.phase) {
    case 'idle':
      return event.type === 'file_selected'
        ? { ...INITIAL_UPLOAD_STATE, phase: 'hashing', totalBytes: event.totalBytes }
        : state;
    case 'hashing':
      if (event.type === 'hash_progress') {
        return { ...state, hashProgress: Math.min(1, Math.max(0, event.progress)) };
      }
      return event.type === 'hash_complete' ? { ...state, phase: 'ready', hashProgress: 1 } : state;
    case 'ready':
      return event.type === 'initiate'
        ? { ...state, phase: 'initiating', sessionId: event.sessionId }
        : state;
    case 'initiating':
      if (event.type === 'session_resolved') return { ...state, sessionId: event.sessionId };
      if (event.type === 'processing') return { ...state, phase: 'processing' };
      return event.type === 'session_created' ? { ...state, phase: 'opening_resumable' } : state;
    case 'opening_resumable':
      if (event.type === 'processing') return { ...state, phase: 'processing' };
      return event.type === 'resumable_opened'
        ? { ...state, phase: 'uploading', completedBytes: event.completedBytes }
        : state;
    case 'uploading':
      if (event.type === 'chunk_committed') {
        const committed =
          event.completedBytes > state.totalBytes ? state.totalBytes : event.completedBytes;
        return { ...state, completedBytes: committed };
      }
      return event.type === 'finalize' ? { ...state, phase: 'finalizing' } : state;
    case 'finalizing':
      return event.type === 'processing' ? { ...state, phase: 'processing' } : state;
    case 'processing':
      return event.type === 'accepted'
        ? { ...state, phase: 'accepted', assetId: event.assetId }
        : state;
    default:
      return state;
  }
}
